import React, { useContext, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CAlert, CButton, CCard, CCardBody, CCardHeader, CCol, CForm, CFormCheck,
  CFormInput, CFormSelect, CFormTextarea, CModal, CModalBody, CModalFooter,
  CModalHeader, CModalTitle, CSpinner, CTable, CTableBody, CTableDataCell,
  CTableHead, CTableHeaderCell, CTableRow,
} from '@coreui/react-pro'

import { api, ApiError } from '../api/client'
import { Person, personLabel, usePeople } from '../api/people'
import { canEdit, canModifyAny, canModifyRow } from '../auth/capabilities'
import { AuthContext } from '../App'

export type FieldType =
  | 'text' | 'textarea' | 'date' | 'time' | 'number' | 'select' | 'switch'
  | 'person'   // single person id (dropdown)
  | 'people'   // list of person ids (checkboxes)
  | 'creator'  // created_by email — admin-only, injected on edit

export interface FieldSpec {
  name: string
  label: string
  type: FieldType
  options?: { value: string; label: string }[]
  required?: boolean
  placeholder?: string
  full?: boolean // full modal row width
  disabled?: boolean // locked control (value still submits)
}

export interface ColumnSpec<T = any> {
  key: string
  label: string
  width?: string // column width — meaningful with fixedLayout
  render?: (row: T) => React.ReactNode
}

interface UserOpt { id: number; email: string; role: string }

const CREATOR_FIELD: FieldSpec = { name: 'created_by', label: 'Creator (admin)', type: 'creator' }

const ACTIONS_WIDTH = 150

// Under fixedLayout the table is sized to max(container, Σ column widths), so
// a width-less column gets only the leftover — which hits zero once the
// container is narrower than the fixed columns and its text stacks one word
// per line. Flooring the table's min-width keeps every unset column at least
// AUTO_COLUMN_FLOOR wide; below that .table-responsive scrolls horizontally.
const AUTO_COLUMN_FLOOR = 240

const fixedTableMinWidth = (columns: ColumnSpec[], actions: boolean) => {
  const specified = columns.reduce((sum, c) => sum + (parseInt(c.width ?? '', 10) || 0), 0)
  const unset = columns.filter((c) => !c.width).length
  return `${specified + (actions ? ACTIONS_WIDTH : 0) + unset * AUTO_COLUMN_FLOOR}px`
}

interface EntityPageProps {
  entity: string
  title: string
  description?: React.ReactNode
  addLabel?: string
  columns: ColumnSpec[]
  fields: FieldSpec[]
  blank: Record<string, any>
  emptyHint?: string
  filter?: (row: any) => boolean
  sortBy?: (a: any, b: any) => number
  /** Rendered in the card header next to the Add button (e.g. a toolbar). */
  toolbar?: React.ReactNode
  /** Rendered under the table with the filtered/sorted rows (e.g. totals). */
  footer?: (rows: any[]) => React.ReactNode
  /** Fixed table layout: columns get their `width`, unset ones share the rest
   *  (never less than 240px each — narrower viewports scroll the table). */
  fixedLayout?: boolean
}

function rowToForm(row: any, fields: FieldSpec[]): Record<string, any> {
  const out: Record<string, any> = {}
  for (const f of fields) {
    const v = row?.[f.name]
    if (f.type === 'time') out[f.name] = v ? String(v).slice(0, 5) : ''
    else if (f.type === 'number') out[f.name] = v == null ? '' : String(v)
    else if (f.type === 'switch') out[f.name] = !!v
    else if (f.type === 'person') out[f.name] = v == null ? '' : String(v)
    else if (f.type === 'people') out[f.name] = Array.isArray(v) ? v : []
    else out[f.name] = v ?? ''
  }
  return out
}

function formToPayload(values: Record<string, any>, fields: FieldSpec[]): Record<string, any> {
  const out: Record<string, any> = {}
  for (const f of fields) {
    let v = values[f.name]
    if (f.type === 'number') {
      v = v === '' || v == null ? null : Number(v)
    } else if (f.type === 'switch') {
      v = !!v
    } else if (f.type === 'person') {
      v = v === '' || v == null ? null : Number(v)
    } else if (f.type === 'people') {
      v = Array.isArray(v) ? v.map(Number) : []
    } else {
      v = v ?? ''
    }
    out[f.name] = v
  }
  return out
}

const EntityPage: React.FC<EntityPageProps> = ({
  entity, title, description, addLabel, columns, fields, blank, emptyHint, filter, sortBy,
  toolbar, footer, fixedLayout,
}) => {
  const queryClient = useQueryClient()
  const { auth } = useContext(AuthContext)
  const role = auth.user?.role
  const editable = canEdit(role)
  const isAdmin = role === 'admin'
  const [modal, setModal] = useState<'closed' | 'add' | 'edit'>('closed')
  const users = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<UserOpt[]>('/v1/admin/users'),
    enabled: isAdmin && modal === 'edit',
  })
  // Creator reassignment is admin-only and only meaningful on edit.
  const editFields = isAdmin ? [...fields, CREATOR_FIELD] : fields
  const activeFields = modal === 'edit' ? editFields : fields
  const [editing, setEditing] = useState<any>(null)
  const [values, setValues] = useState<Record<string, any>>({})
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<any>(null)

  const list = useQuery({
    queryKey: [entity],
    queryFn: () => api.get<any[]>(`/v1/${entity}`),
  })
  // For 'person' / 'people' fields — same queryKey as the People page, so it dedupes.
  const people = usePeople()
  const peopleRows: Person[] = people.data ?? []

  const save = useMutation({
    mutationFn: (payload: { id: number | null; body: any }) =>
      payload.id == null
        ? api.post(`/v1/${entity}`, payload.body)
        : api.put(`/v1/${entity}/${payload.id}`, payload.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [entity] })
      setModal('closed')
      setError(null)
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Save failed. Try again.')
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/v1/${entity}/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [entity] })
      setConfirmDelete(null)
    },
  })

  const openAdd = () => {
    setEditing(null)
    setValues(rowToForm(blank, fields))
    setError(null)
    setModal('add')
  }

  const openEdit = (row: any) => {
    setEditing(row)
    setValues(rowToForm(row, editFields))
    setError(null)
    setModal('edit')
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    save.mutate({ id: editing?.id ?? null, body: formToPayload(values, activeFields) })
  }

  const rows = useMemo(
    () => [...(list.data ?? [])].filter(filter ?? (() => true)).sort(sortBy ?? (() => 0)),
    [list.data, filter, sortBy],
  )

  const setField = (name: string, v: any) =>
    setValues((prev) => ({ ...prev, [name]: v }))

  return (
    <>
      <CCard className="mb-4">
        <CCardHeader className="d-flex align-items-center">
          <div>
            <h4 className="mb-0">{title}</h4>
            {description && (
              <div className="small text-body-secondary">{description}</div>
            )}
          </div>
          <div className="ms-auto d-flex align-items-center flex-wrap gap-2">
            {toolbar}
            {editable && (
              <CButton color="primary" onClick={openAdd}>
                {addLabel ?? 'Add'}
              </CButton>
            )}
          </div>
        </CCardHeader>
        <CCardBody>
          {list.isLoading ? (
            <div className="text-center py-4"><CSpinner /></div>
          ) : list.isError ? (
            <CAlert color="danger">Couldn't load {title}. Refresh the page.</CAlert>
          ) : rows.length === 0 ? (
            <div className="text-center text-body-secondary py-4">
              {emptyHint ?? 'Nothing here yet — add the first row.'}
            </div>
          ) : (
            <div className="table-responsive">
              <CTable
                hover align="middle" className="mb-0"
                style={fixedLayout
                  ? { tableLayout: 'fixed', minWidth: fixedTableMinWidth(columns, editable) }
                  : undefined}
              >
                <CTableHead>
                  <CTableRow>
                    {columns.map((c) => (
                      <CTableHeaderCell
                        key={c.key} scope="col"
                        style={c.width ? { width: c.width } : undefined}
                      >
                        {c.label}
                      </CTableHeaderCell>
                    ))}
                    {editable && (
                      <CTableHeaderCell
                        key="__actions" scope="col" className="text-end"
                        style={fixedLayout ? { width: `${ACTIONS_WIDTH}px` } : undefined}
                      >
                        Actions
                      </CTableHeaderCell>
                    )}
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {rows.map((row) => (
                    <CTableRow key={row.id}>
                      {columns.map((c) => (
                        <CTableDataCell key={c.key}>
                          {c.render ? c.render(row) : (row[c.key] ?? '')}
                        </CTableDataCell>
                      ))}
                      {editable && (
                        <CTableDataCell className="text-end">
                          {canModifyRow(role, row.created_by, auth.user?.email) && (
                            <CButton
                              size="sm" color="secondary" variant="outline"
                              className="me-2" onClick={() => openEdit(row)}
                            >
                              Edit
                            </CButton>
                          )}
                          {canModifyAny(role) && (
                            <CButton
                              size="sm" color="danger" variant="outline"
                              onClick={() => setConfirmDelete(row)}
                            >
                              Delete
                            </CButton>
                          )}
                        </CTableDataCell>
                      )}
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          )}
          {footer && rows.length > 0 && footer(rows)}
        </CCardBody>
      </CCard>

      {/* Add / Edit modal */}
      <CModal visible={modal !== 'closed'} onClose={() => setModal('closed')} size="lg">
        <CModalHeader>
          <CModalTitle>
            {modal === 'edit' ? `Edit ${title.replace(/s$/, '')}` : `Add to ${title}`}
          </CModalTitle>
        </CModalHeader>
        <CForm onSubmit={onSubmit} className="row g-3 mb-0">
          <CModalBody>
            {error && <CAlert color="danger" className="mb-3">{error}</CAlert>}
            <div className="row g-3">
              {activeFields.map((f) => (
                <CCol md={f.full || f.type === 'textarea' ? 12 : 6} key={f.name}>
                  {f.type === 'select' ? (
                    <CFormSelect
                      id={f.name}
                      label={f.label}
                      value={values[f.name] ?? ''}
                      onChange={(e) => setField(f.name, e.target.value)}
                      required={f.required}
                      disabled={f.disabled}
                    >
                      {(f.options ?? []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </CFormSelect>
                  ) : f.type === 'textarea' ? (
                    <CFormTextarea
                      id={f.name}
                      label={f.label}
                      rows={2}
                      value={values[f.name] ?? ''}
                      onChange={(e) => setField(f.name, e.target.value)}
                      placeholder={f.placeholder}
                    />
                  ) : f.type === 'switch' ? (
                    <CFormCheck
                      id={f.name}
                      label={f.label}
                      checked={!!values[f.name]}
                      onChange={(e) => setField(f.name, e.target.checked)}
                    />
                  ) : f.type === 'creator' ? (
                    <CFormSelect
                      id={f.name}
                      label={f.label}
                      value={values[f.name] ?? ''}
                      onChange={(e) => setField(f.name, e.target.value)}
                    >
                      <option value="">— (stays with current creator)</option>
                      {(users.data ?? []).map((u) => (
                        <option key={u.id} value={u.email}>{u.email}</option>
                      ))}
                    </CFormSelect>
                  ) : f.type === 'person' ? (
                    <CFormSelect
                      id={f.name}
                      label={f.label}
                      value={values[f.name] ?? ''}
                      onChange={(e) => setField(f.name, e.target.value)}
                      required={f.required}
                      disabled={f.disabled}
                    >
                      <option value="">—</option>
                      {peopleRows.map((p) => (
                        <option key={p.id} value={p.id}>{personLabel(p)}</option>
                      ))}
                    </CFormSelect>
                  ) : f.type === 'people' ? (
                    <div>
                      <div className="fw-semibold small mb-2" style={{ paddingTop: 8 }}>{f.label}</div>
                      {peopleRows.length === 0 && (
                        <div className="small text-body-secondary">
                          No people yet — add them under 👥 People first.
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-3">
                        {peopleRows.map((p) => {
                          const ids: number[] = values[f.name] ?? []
                          const checked = ids.includes(p.id)
                          return (
                            <CFormCheck
                              key={p.id}
                              id={`${f.name}-${p.id}`}
                              label={personLabel(p)}
                              checked={checked}
                              onChange={() =>
                                setField(
                                  f.name,
                                  checked ? ids.filter((i) => i !== p.id) : [...ids, p.id],
                                )
                              }
                            />
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <CFormInput
                      id={f.name}
                      label={f.label}
                      type={f.type}
                      value={values[f.name] ?? ''}
                      onChange={(e) => setField(f.name, e.target.value)}
                      required={f.required}
                      placeholder={f.placeholder}
                    />
                  )}
                </CCol>
              ))}
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" variant="outline" onClick={() => setModal('closed')}>
              Cancel
            </CButton>
            <CButton color="primary" type="submit" disabled={save.isPending}>
              {save.isPending ? <CSpinner size="sm" /> : 'Save'}
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>

      {/* Delete confirmation */}
      <CModal visible={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <CModalHeader>
          <CModalTitle>Delete this row?</CModalTitle>
        </CModalHeader>
        <CModalBody>
          This can't be undone. Are you sure?
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setConfirmDelete(null)}>
            Cancel
          </CButton>
          <CButton
            color="danger"
            disabled={remove.isPending}
            onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}
          >
            {remove.isPending ? <CSpinner size="sm" /> : 'Delete'}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default EntityPage
